import './Settings/Settings.css'
import './Workbench.css'
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Operators } from './Settings/Operators.tsx';
import { seedFromUrl } from './UrlProcessor/ProcessUrl.tsx';
import { newSeed } from '../Shared/Helpers.tsx';
import { differentiationPlan, tierSeed } from './Differentiation.ts';
import { classSetSheets, pageCount, parseRoster, problemCount } from './ClassSet.ts';
import { worksheetEventParams } from './worksheetEventParams.ts';
import { RailSection, SettingsContainer } from './Settings/SettingsContainer.tsx';
import { SettingsProvider } from './Settings/SettingsStore.tsx';
import { withPerPageClamped } from './Settings/perPageOptions.ts';
import { NewProblemsButton, RebuildForm } from './Settings/WorksheetIdentity.tsx';
import { ShareWorksheet } from './Settings/ShareWorksheet.tsx';
import { generateUrl } from './UrlProcessor/GenerateUrl.tsx';
import { ClassRoster } from './Settings/ClassRoster.tsx';
import { FluencyChartSection } from './Settings/FluencyChartSection.tsx';
import { ChipStrip } from './Settings/ChipStrip.tsx';
import { Seo } from '../Shared/Seo/Seo.tsx';
import { useCanPreviewPdf } from '../Shared/Pdf/useCanPreviewPdf.ts';
import { PdfCanvasPlaceholder } from '../Shared/Pdf/PdfPlaceholders.tsx';
import { useRememberWorksheet } from '../Shared/useRememberWorksheet.ts';
import { useNarrowViewport } from '../Shared/useNarrowViewport.ts';
import { RecentWorksheetsPanel } from '../Shared/RecentWorksheetsPanel.tsx';
import { BlueprintCorners } from '../Shared/Theme/Blueprint.tsx';
import { ColorByNumberFinished } from './ColorByNumberFinished.tsx';
import { canPresent } from './Present/presentable.ts';
import type { PresentCard } from './Present/presentCards.ts';
import { track } from '../Shared/analytics.ts';

import { RouteSeo } from '../Shared/Seo/routeSeo.ts';
import { showsOperatorBar, ProblemFormatOptions } from './Constants.ts';
import { labelFor } from './Labels.ts';
import { useTranslation } from '../Shared/i18n/useTranslation.ts';
import { format } from '../Shared/i18n/format.ts';

// Loaded on demand: this is the only path into @react-pdf/renderer, which
// dominates the bundle and is not needed to render the page itself.
const WorksheetPreview = lazy(() => import('./PdfGenerator/WorksheetPreview.tsx'));

// The on-screen view (ADR 0091) is loaded when the button is pressed, along with
// the code that builds its cards, so neither is in the page's first load.
const PresentMode = lazy(() => import('./Present/PresentMode.tsx'));

/**
 * The width below which the rail becomes the bottom sheet (ADR 0061 §7). The
 * CSS switches at the same 899px; this is the one place script needs to know.
 */
export const WORKBENCH_NARROW = '(max-width: 899px)';

/*
 * The rail's controls, memoised: a parent re-render that changes no setting -
 * the phone sheet opening, a PDF arriving, a page count landing - no longer
 * re-renders them. `SettingsProvider` keeps the context value stable for that
 * (ADR 0074 §2), and the controls read the settings through `useSetting`, so a
 * change re-renders only those whose slice changed (ADR 0081).
 */
const MemoOperators = React.memo(Operators);
const MemoSettingsContainer = React.memo(SettingsContainer);
const MemoClassRoster = React.memo(ClassRoster);
const MemoFluencyChartSection = React.memo(FluencyChartSection);
const MemoChipStrip = React.memo(ChipStrip);

/** e.g. "long-division-worksheet.pdf" — so a saved file is recognisable later. */
function downloadFileName(operationTitle: string, wordProblems: boolean): string {
    const slug = operationTitle.toLowerCase().replace(/\s+/g, '-');
    return slug + (wordProblems ? '-word-problems.pdf' : '-worksheet.pdf');
}

/**
 * The generator, laid out as a workbench (ADR 0061).
 *
 * - A page header: kicker, the page's one <h1>, a lede, and two tags naming the
 *   sheet's code and how much paper it is.
 * - A settings rail on the left, sticky, holding the operator bar and the
 *   settings panel in one blueprint card, with the class roster beneath it.
 * - The worksheet on the right, always in view, with its actions directly above
 *   it and a strip under it that carries the code and "Rebuild".
 *
 * Below 900px the rail is not a second copy of the settings: the same <aside>
 * becomes a bottom sheet, opened by the "Options" button or one of the chips
 * under the header (§7). One DOM means one set of ids, one focus order, and
 * one place a new control can be added.
 *
 * `title` and `lede` come from the landing page when there is one - the h1 is
 * the manifest's, which is what the prerendered HTML carries too. Standing
 * alone (a slug missing from the manifest, or a test) the generator titles
 * itself after the operation, as it always has.
 */
export function MathFactsGenerator({
    initialSettings,
    embedded = false,
    title,
    lede,
    sharePath,
}: {
    initialSettings: any;
    embedded?: boolean;
    title?: string;
    lede?: string;
    /**
     * The page "Copy link" points at, and the page a recent-worksheets entry
     * reopens on: the landing page's slug, handed down by the page that knows
     * it reads a worksheet link back. Without one both open on the home page,
     * as they always have.
     */
    sharePath?: string;
}) {
    const t = useTranslation();
    const [ chosenSettings, setSettingsRaw ] = useState(initialSettings);
    // What the sheet is built from: the visitor's settings with the per-page
    // count held to what the format offers (ADR 0082). Derived, so every reader
    // - the panel, the preview, the link, the PDF - sees the same count and no
    // control has to correct it as a side effect of drawing itself.
    const settings = useMemo(() => withPerPageClamped(chosenSettings), [chosenSettings]);
    // The seed is the worksheet's identity. A link may pin one via `s=`;
    // otherwise this load gets a fresh one. Any settings change draws a new seed
    // — a visitor who ticks Subtraction expects a new sheet (ADR 0009 §3).
    const [ seed, setSeed ] = useState(() =>
        seedFromUrl(typeof window !== 'undefined' ? window.location.search : ''));
    // The class roster. Component state only — never persisted or encoded
    // anywhere, by design (ADR 0011 §1). Changing it does not reseed.
    const [ roster, setRoster ] = useState('');
    // A link that ends in `present=1` opens on the board (ADR 0091). It is a
    // wish held until the sheet can be shown, and dropped the moment the visitor
    // changes anything: a link to a sheet that cannot be shown must not open a
    // board when they later pick a format that can.
    const opensOnBoard = useRef(
        typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('present') === '1');
    const setSettings = useCallback((next) => {
        opensOnBoard.current = false;
        setSettingsRaw(next);
        setSeed(newSeed());
    }, []);
    const operationTitle =
        settings.Format === ProblemFormatOptions.PlaceValue
            ? labelFor(ProblemFormatOptions.PlaceValue)
            : settings.Format === ProblemFormatOptions.TellingTime
            ? labelFor(ProblemFormatOptions.TellingTime)
            : settings.Format === ProblemFormatOptions.OrderOfOperations
            ? labelFor(ProblemFormatOptions.OrderOfOperations)
            : settings.AllOperations ? labelFor(ProblemFormatOptions.MathFacts) : labelFor(settings.Page)
    const wordProblems = settings.Format === ProblemFormatOptions.WordProblem;
    const pageTitle = wordProblems ?
        operationTitle + " Word Problem Worksheet Generator" :
        operationTitle + " Worksheet Generator";

    const seoEntry: RouteSeo = {
        slug: typeof window !== 'undefined' ? window.location.pathname : '/',
        title: pageTitle + " | Math Pages",
        description: `Create and print free ${operationTitle.toLowerCase()} worksheets. ` +
            `Choose number ranges, set problems per page, and download a printable PDF with an answer key.`,
    };

    const canPreview = useCanPreviewPdf();
    useRememberWorksheet(settings, seed, sharePath);

    // A differentiation set is three documents in one PDF — one step easier, the
    // sheet as configured, one step harder — grouped by tier (ADR 0010). The
    // middle tier draws from the base seed, so it matches the ordinary preview.
    const diffPlan = settings.DifferentiationSet ? differentiationPlan(settings) : null;
    const sheets = diffPlan
        ? diffPlan.tiers.map((tier, index) => ({
              settings: tier.settings,
              seed: tierSeed(seed, tier.role),
              footerSeed: `${seed}-${index + 1}`,
          }))
        : undefined;
    const tierShortfall =
        diffPlan && (diffPlan.floorReached || diffPlan.ceilingReached)
            ? format(t('settings.tierShortfall'), {
                  level: diffPlan.floorReached ? t('settings.easiest') : t('settings.hardest'),
                  n: diffPlan.tiers.length,
              })
            : null;

    // A class set: one sheet per student. Takes precedence over a differentiation
    // set — combining the two is out of scope (ADR 0011 Out of scope).
    const rosterEntries = parseRoster(roster).entries;
    const classSet = !diffPlan && rosterEntries.length > 0
        ? classSetSheets(settings, seed, rosterEntries)
        : null;
    const previewSheets = classSet ?? sheets;

    // ─── How much paper, for the header tags and the strip ───────────────
    // Everything the PDF holds: a differentiation set's three tiers, a class
    // set's sheets, and the answer pages. The page count is the finished
    // file's own once a build lands (a worked key re-flows, so only the build
    // knows its length); while one runs, it is counted from the settings.
    const [ builtPages, setBuiltPages ] = useState<number | null>(null);
    const printedSheets = previewSheets ?? [{ settings, seed }];
    const pages = builtPages ?? pageCount(printedSheets);
    const pagesText = pages === 1 ? t('workbench.onePage') : format(t('workbench.pages'), { n: pages });
    const problems = problemCount(printedSheets);
    const problemsText = problems === 1
        ? t('workbench.oneProblem')
        : format(t('workbench.problems'), { n: problems });
    // A spiral review's count is its sections', not the per-page setting's, and a riddle's is
    // its punchline's, which differs from page to page (ADR 0088 §5): neither is the setting.
    const paperTag = settings.Format === ProblemFormatOptions.SpiralReview ||
        settings.Format === ProblemFormatOptions.RiddleDecoder
        ? pagesText
        : `${pagesText} · ${problemsText}`;

    // ─── The sheet on a screen (ADR 0091) ───────────────────────────────
    // Offered only for a single sheet of a format whose text matches its PDF.
    // The cards are built when the button is pressed, from the seed the sheet
    // was printed with, so what is on the board is what is on the page.
    const showable = canPresent(settings, { differentiationSet: !!diffPlan, classSet: !!classSet });
    const [ presenting, setPresenting ] = useState<PresentCard[] | null>(null);
    const openPresent = useCallback(async () => {
        const { presentCards } = await import('./Present/presentCards.ts');
        const cards = presentCards(settings, seed);
        if (cards) {
            track('sheet_presented', { document_type: 'worksheet', ...worksheetEventParams(settings) });
            setPresenting(cards);
        }
    }, [settings, seed]);
    const closePresent = useCallback(() => setPresenting(null), []);
    useEffect(() => {
        if (opensOnBoard.current && showable) {
            opensOnBoard.current = false;
            openPresent();
        }
    }, [showable, openPresent]);

    // ─── The bottom sheet (ADR 0061 §7) ─────────────────────────────────
    const narrow = useNarrowViewport(WORKBENCH_NARROW);
    const [ optionsOpen, setOptionsOpen ] = useState(false);
    const [ optionsAnchor, setOptionsAnchor ] = useState<string | null>(null);
    const rail = useRef<HTMLElement>(null);
    const opener = useRef<HTMLElement | null>(null);

    const openOptions = useCallback((anchor?: string) => {
        opener.current = document.activeElement as HTMLElement | null;
        setOptionsAnchor(anchor ?? null);
        setOptionsOpen(true);
    }, []);
    const closeOptions = useCallback(() => setOptionsOpen(false), []);

    // Widening the window past the breakpoint turns the sheet back into the
    // rail; an "open" flag left behind would come back as a stuck scrim.
    useEffect(() => {
        if (!narrow) {
            setOptionsOpen(false);
        }
    }, [narrow]);

    useEffect(() => {
        if (!optionsOpen) {
            return;
        }
        // The class first, then the reads: the other way round, reading
        // offsetTop laid the page out and the class change made the next
        // frame lay it out again.
        document.body.classList.add('optionsSheetOpen');
        const sheet = rail.current;
        const target = optionsAnchor ? document.getElementById(optionsAnchor) : null;
        if (target && sheet) {
            sheet.scrollTop = Math.max(0, target.offsetTop - 64);
        } else if (sheet) {
            sheet.scrollTop = 0;
        }
        (target?.querySelector<HTMLElement>('button, input, select, textarea, [tabindex]')
            ?? sheet?.querySelector<HTMLElement>('.railSheetTitle'))?.focus({ preventScroll: true });

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOptionsOpen(false);
            }
        };
        document.addEventListener('keydown', onKey);
        const returnTo = opener.current;
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.classList.remove('optionsSheetOpen');
            returnTo?.focus?.();
        };
    }, [optionsOpen, optionsAnchor]);

    // The sheet is a modal dialog while it is open on a narrow screen, and
    // inert while it is shut there, so Tab never walks into a panel nobody can
    // see. On a wide screen it is simply the rail. `inert` is passed as a
    // string: React 18 forwards unknown lowercase attributes as they are.
    const sheetMode = narrow;
    const railProps = sheetMode
        ? optionsOpen
            ? { role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'railSheetTitle' }
            : { inert: '' }
        : { 'aria-label': t('workbench.settingsAria') };
    const pageInert = sheetMode && optionsOpen ? { inert: '' } : {};

    // Shared by the preview and its placeholder: on a phone the toolbar is a
    // strip below the sheet, and the placeholder only holds its place if it has
    // the same items in it.
    const sheetActions = (
        <>
            <NewProblemsButton/>
            {showable && (
                <button type='button' className='btn btn-secondary presentButton' onClick={openPresent}>
                    {t('workbench.presentOpen')}
                </button>
            )}
            <ShareWorksheet path={sharePath}/>
        </>
    );

    const operators = showsOperatorBar(settings.Format);
    const summary = [
        operators && settings.Operators?.length
            ? settings.Operators.map((o) => labelFor(o.value, settings.Language)).join(' + ')
            : labelFor(settings.Format, settings.Language),
        settings.Format !== ProblemFormatOptions.SpiralReview
            ? format(t('workbench.perPageChip'), { n: settings.NumberOfProblemsPerPage })
            : null,
    ].filter(Boolean).join(' · ');

    return(
        <SettingsProvider settings={settings} setSettings={setSettings} seed={seed} setSeed={setSeed}>
            {!embedded && <Seo entry={seoEntry}/>}
            {presenting && (
                <Suspense fallback={null}>
                    <PresentMode cards={presenting} code={seed} link={generateUrl(settings, seed, sharePath, true)} onClose={closePresent}/>
                </Suspense>
            )}
            <div className='workbench'>
                <header className='workbenchHeader' {...pageInert}>
                    <div className='workbenchTitle'>
                        <div className='kicker'>{t('workbench.kicker')}</div>
                        {/* A landing page passes its manifest h1; embedded without one, the
                            page around it owns the heading. */}
                        {(title ?? (embedded ? null : pageTitle)) && <h1>{title ?? pageTitle}</h1>}
                        <p className='workbenchLede'>{lede ?? t('workbench.lede')}</p>
                    </div>
                    <div className='workbenchTags'>
                        <span className='tag tag-neutral'>{format(t('workbench.sheetTag'), { code: seed })}</span>
                        <span className='tag tag-outline'>{paperTag}</span>
                    </div>
                </header>

                <MemoChipStrip onOpen={openOptions}/>

                <div className='workbenchBody'>
                    {optionsOpen && <div className='railScrim' onClick={closeOptions}/>}
                    <aside
                        ref={rail}
                        id='worksheetSettings'
                        className={'workbenchRail' + (optionsOpen ? ' isOpen' : '')}
                        {...railProps}
                    >
                        {/* Sheet chrome: only drawn below 900px. */}
                        <div className='railSheetHead'>
                            <div className='railSheetHandle' aria-hidden='true'/>
                            <div className='railSheetTitleRow'>
                                <div id='railSheetTitle' className='railSheetTitle' tabIndex={-1}>
                                    {t('workbench.options')}
                                </div>
                                <span className='railSummary'>{summary}</span>
                            </div>
                        </div>

                        <div className='railCard card blueprint'>
                            <BlueprintCorners/>
                            {/* Place value asks about one number, so there is no
                                operation to choose and the operator bar would only
                                be noise. */}
                            {operators && (
                                <RailSection id='rail-operation' title={t('workbench.operation')}>
                                    <MemoOperators/>
                                </RailSection>
                            )}
                            <MemoSettingsContainer/>
                        </div>

                        {/* The class set sits under the card rather than in the
                            advanced tier (ADR 0061 §3). It is collapsed by
                            default, and a typed roster announces itself through
                            the page count under the sheet. */}
                        <MemoClassRoster roster={roster} onRosterChange={setRoster}/>

                        {/* The tracking chart for a level sheet (ADR 0071 §8). It reads
                            the roster as a string, so the names stay in this component's
                            state and the settings context never sees one. */}
                        <MemoFluencyChartSection roster={roster}/>

                        <div className='railSheetFoot'>
                            <button type='button' className='btn btn-primary btn-block blueprint' onClick={closeOptions}>
                                <BlueprintCorners/>
                                {t('workbench.showWorksheet')}
                            </button>
                        </div>
                    </aside>

                    <div className='workbenchMain' {...pageInert}>
                        {tierShortfall && (
                            <p className="differentiationNotice" role="status">{tierShortfall}</p>
                        )}
                        {/* What the sheet looks like finished (ADR 0058 amendment). */}
                        {settings.Format === ProblemFormatOptions.ColorByNumber && <ColorByNumberFinished/>}
                        <Suspense fallback={<SheetPlaceholder canPreview={canPreview} onOpenOptions={() => openOptions()} actions={sheetActions}/>}>
                            <WorksheetPreview
                                settings={settings}
                                seed={seed}
                                sheets={previewSheets}
                                canPreview={canPreview}
                                fileName={downloadFileName(operationTitle, wordProblems)}
                                eventParams={{
                                    ...worksheetEventParams(settings),
                                    // Caller state, not a setting: a class set comes
                                    // from the roster box, not from ISettingsProps.
                                    class_set: classSet !== null,
                                }}
                                actions={sheetActions}
                                onOpenOptions={() => openOptions()}
                                onPageCount={setBuiltPages}
                            />
                        </Suspense>

                        <div className='sheetStrip'>
                            <span className={classSet ? 'classSetPageCount' : undefined} role={classSet ? 'status' : undefined}>
                                {pagesText} · {format(t('workbench.sheetStatus'), { code: seed })}
                            </span>
                            <RebuildForm/>
                        </div>

                        <RecentWorksheetsPanel/>
                    </div>
                </div>
            </div>
        </SettingsProvider>
    );
}

/**
 * What stands in for the sheet while the PDF chunk loads: a blank page of the
 * right shape, so nothing below it jumps when the preview lands, and the
 * narrow-screen bar with a working "Options" button, so the settings are
 * reachable before the renderer is.
 *
 * "The right shape" depends on which preview is coming (ADR 0067 §2). Where
 * the browser shows PDFs inline it is the iframe's page-shaped frame. Where it
 * does not - every phone - WorksheetPreview draws a padded frame around a
 * canvas, which is a few pixels shorter, so this draws that frame around the
 * canvas's own placeholder. WorksheetPreview is the other copy of both shapes.
 *
 * The toolbar is the third. On a phone it is a strip below the sheet with Print
 * hidden, so what takes up its room is Download and the share controls; a
 * placeholder holding only Print left the strip empty and the sheet 47px
 * shorter until the preview landed.
 */
function SheetPlaceholder({ canPreview, onOpenOptions, actions }: { canPreview: boolean; onOpenOptions: () => void; actions: React.ReactNode }) {
    const t = useTranslation();
    return (
        <div className='sheetView'>
            <div className='sheetToolbar'>
                <span className='btn btn-primary blueprint sheetToolbarPrimary' aria-disabled='true'>
                    <BlueprintCorners/>
                    {t('workbench.buildingPdf')}
                </span>
                <span className='btn btn-secondary' aria-disabled='true'>{t('workbench.downloadPdf')}</span>
                {actions}
            </div>
            {canPreview ? (
                <div className='sheetFrame blueprint sheetFrame--placeholder'>
                    <BlueprintCorners/>
                    <p className='pdfLoading'>{t('settings.loadingPreview')}</p>
                </div>
            ) : (
                <div className='sheetFrame blueprint sheetFrame--canvas'>
                    <BlueprintCorners/>
                    <PdfCanvasPlaceholder/>
                </div>
            )}
            <div className='workbenchBar'>
                <span className='btn btn-primary blueprint workbenchBarPrimary' aria-disabled='true'>
                    <BlueprintCorners/>
                    {t('workbench.buildingPdf')}
                </span>
                <button type='button' className='btn btn-secondary workbenchBarOptions' onClick={onOpenOptions}>
                    {t('workbench.options')}
                </button>
            </div>
        </div>
    );
}
